import {
  ICreatorInsights,
  ITopPerformingContent,
} from "@shared/lib/types/influencer";
import { formatData } from "@shared/lib/utils/formatData";

export interface ScriptTopPostView {
  key: string;
  title: string;
  url?: string;
  viewsLabel?: string;
  viewsLift?: string;
  erLabel?: string;
  erLift?: string;
  description?: string;
}

export interface ScriptContentAnalysisView {
  contentStyle: string[];
  whatWorks: string[];
  whatToAvoid: string[];
  topPerforming: ScriptTopPostView[];
}

function textOf(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function asStringList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((item) => textOf(item)).filter(Boolean);
  }
  const text = textOf(value);
  return text ? [text] : [];
}

function compact(values: unknown[]): string[] {
  return values.map((value) => textOf(value)).filter(Boolean);
}

function firstList(...candidates: (string[] | undefined)[]): string[] {
  for (const list of candidates) {
    if (list && list.length > 0) return list;
  }
  return [];
}

function formatLift(value: unknown): string | undefined {
  if (typeof value === "number" && Number.isFinite(value)) {
    const prefix = value > 0 ? "+" : "";
    return `${prefix}${Math.round(value)}% vs baseline`;
  }
  const trimmed = textOf(value);
  if (!trimmed) return undefined;
  if (/vs baseline/i.test(trimmed)) return trimmed;
  if (/[%×x]/i.test(trimmed)) return `${trimmed} vs baseline`;
  return trimmed;
}

function formatViews(value: unknown): string | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return String(formatData(value));
  return textOf(value) || undefined;
}

function formatEr(value: unknown): string | undefined {
  if (typeof value === "number" && Number.isFinite(value)) {
    const percent = Math.abs(value) <= 1 ? value * 100 : value;
    const formatted = Number.isInteger(percent) ? String(percent) : percent.toFixed(1);
    return `${formatted}%`;
  }
  const text = textOf(value);
  if (!text) return undefined;
  return text.includes("%") ? text : `${text}%`;
}

function mapTopPost(item: ITopPerformingContent | null | undefined, index: number): ScriptTopPostView {
  const title = textOf(item?.title) || textOf(item?.caption) || `Post ${index + 1}`;
  return {
    key: `${title}-${index}`,
    title,
    url: textOf(item?.url) || textOf(item?.link) || textOf(item?.permalink) || undefined,
    viewsLabel: formatViews(item?.views ?? item?.view_count),
    viewsLift: formatLift(item?.views_vs_baseline ?? item?.views_vs_baseline_pct),
    erLabel: formatEr(item?.engagement_rate ?? item?.er),
    erLift: formatLift(item?.er_vs_baseline ?? item?.er_vs_baseline_pct),
    description: textOf(item?.description) || textOf(item?.what_worked) || undefined,
  };
}

export function deriveContentAnalysis(
  analysis?: ICreatorInsights
): ScriptContentAnalysisView | undefined {
  if (!analysis) return undefined;

  const nested = analysis.content_analysis;
  const contentStyle = firstList(
    asStringList(analysis.content_style),
    asStringList(nested?.content_style),
    compact([analysis.overall_creator_persona?.authenticity_summary])
  );
  const whatWorks = firstList(
    asStringList(analysis.what_works),
    asStringList(nested?.what_works),
    compact([analysis.overall_brand_fit?.brand_fit_summary])
  );
  const whatToAvoid = firstList(
    asStringList(analysis.what_to_avoid),
    asStringList(nested?.what_to_avoid),
    asStringList(analysis.overall_safety?.safety_notes)
  );
  const rawTop = analysis.top_performing_content ?? nested?.top_performing_content;
  const topPerforming = (Array.isArray(rawTop) ? rawTop : []).slice(0, 3).map(mapTopPost);

  if (
    !contentStyle.length &&
    !whatWorks.length &&
    !whatToAvoid.length &&
    !topPerforming.length
  ) {
    return undefined;
  }

  return { contentStyle, whatWorks, whatToAvoid, topPerforming };
}
